/**
 * Profile management functions for authentication orchestrator
 * Handles profile data setting and submission
 */

import type { AuthPhase } from '@/types/profileTypes';
import { PortfolioUpdateType, type UpdatePortfolioMutation } from '@/generated/nexus-graphql';
import { MutationTypes as UserMutationTypes } from '@/store/modules/user/mutations';
import { profileStorage } from '@/services/profileStorage';
import { waitForToken } from '@/utils/tokenRetry';
import type { Store } from 'vuex';
import nexusClient from '@/apollo/nexusClient';
import {
  SET_MY_PROFILE_SLUG_MUTATION,
  SetMyProfileSlugResponse
} from '@/services/graphql/nexus/public-profile';
import type { NavigationService } from './useAuthNavigationService';
import type { AuthOrchestratorState } from './useAuthState';
import type { RootState, PortfolioCompletionStatus } from '../types';
import { ERROR_MESSAGES } from '../constants';
import { createDebugLogger } from '../utils/debug';

const logger = createDebugLogger('AuthProfile');

// Extended GeneralProfileData for auth purposes
// The base type from profileTypes doesn't include email/phone, but auth flow needs them
export interface GeneralProfileData {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
  userTypes: string[] | readonly string[];
  slug?: string; // Profile URL slug
}

export interface TeacherProfileData {
  id?: string; // Optional for local state, required by GraphQL type
  gradeLevel: string[];
  schoolName: string;
  district: string;
  city: string;
  state: string;
  subjects?: string[];
}

export interface StudentProfileData {
  id?: string; // Optional for local state, required by GraphQL type
  gradeLevel: string;
  schoolName: string;
  district: string;
  city: string;
  state: string;
  gender?: string;
  ethnicity?: string[];
}

export interface ParentProfileData {
  id?: string; // Optional for local state, required by GraphQL type
  email?: string; // Required by GraphQL type
  parent?: Record<string, unknown>; // Required by GraphQL type
  childGradeLevel: string[];
  schoolName: string;
  district: string;
  city: string;
  state: string;
  currentEmployer?: string;
  positionTitle?: string;
}

export interface EmployerProfileData {
  id?: string; // Optional for local state, required by GraphQL type
  jobTitle?: string; // Required by GraphQL type
  work?: Record<string, unknown>; // Required by GraphQL type
  companyName: string;
  industry: string;
  title: string;
  primaryProduct?: string;
}

export interface SchoolProfileData {
  id?: string; // Optional for local state, required by GraphQL type
  schoolName: string;
  district: string;
  city: string;
  state: string;
  position?: string;
}

export interface OrganizerProfileData {
  id?: string; // Optional for local state, required by GraphQL type
  jobTitle?: string; // Required by GraphQL type
  organizerName?: string; // Required by GraphQL type
  work?: Record<string, unknown>; // Required by GraphQL type
  organizationName: string;
  role: string;
  industry?: string;
  primaryProduct?: string;
}

export interface UpdatePortfolioInput {
  type: PortfolioUpdateType;
  data: Record<string, unknown>;
}

export function useAuthProfile(
  state: AuthOrchestratorState,
  deps: {
    updatePortfolioMutate: (params: { input: UpdatePortfolioInput }) => Promise<{
      data?: UpdatePortfolioMutation;
      errors?: Array<{ message: string }>;
    }>;
    rootStore?: Store<RootState>;
    requestEmailLinking?: (email: string) => Promise<void>;
    requestPhoneLinking?: (phoneNumber: string) => Promise<void>;
    nextPhase?: () => void;
    completeAndRedirect?: () => void;
    navigationService?: NavigationService;
  }
) {
  /**
   * Determine which profile phase is incomplete
   */
  const determineIncompletePhase = (
    completionStatus: PortfolioCompletionStatus | null | undefined,
    userTypes: string[],
    hasAlternateContact?: boolean,
    alternateContactSkipped?: boolean
  ): AuthPhase | null => {
    // Check if user needs to add alternate contact first (for new accounts)
    // This should happen before general profile
    if (!hasAlternateContact && !alternateContactSkipped) {
      // User doesn't have alternate contact and hasn't explicitly skipped it
      // This ensures new users see the alternateContact phase
      return 'alternateContact';
    }

    // Check general profile next
    if (!completionStatus?.generalProfile) {
      return 'generalProfile';
    }

    // Wayne MVP: 'None / Just Joining' is a sentinel meaning the participant
    // doesn't need a role-specific portfolio. Filter it out before the loop so
    // a participant who picked only 'None' lands straight in the program, and
    // a participant who picked 'None' alongside other roles still only gates
    // on the real roles.
    const roleUserTypes = userTypes.filter(t => t !== 'None');
    if (roleUserTypes.length === 0) {
      return null;
    }

    // Check each role-specific profile and find the first incomplete one
    let firstIncompleteIndex = -1;
    let firstIncompleteRole: string | null = null;

    for (let i = 0; i < roleUserTypes.length; i++) {
      const userType = roleUserTypes[i];
      const roleKey = `${userType.toLowerCase()}Profile`;

      if (completionStatus[roleKey] === false) {
        if (firstIncompleteIndex === -1) {
          firstIncompleteIndex = i;
          firstIncompleteRole = userType;
        }
      }
    }

    if (firstIncompleteRole) {
      // Set currentRoleIndex to the first incomplete role
      state.currentRoleIndex = firstIncompleteIndex;
      logger.log(
        `Setting currentRoleIndex to ${firstIncompleteIndex} for incomplete ${firstIncompleteRole} profile`
      );
      return `${firstIncompleteRole.toLowerCase()}Profile` as AuthPhase;
    }

    // All profiles complete
    return null;
  };

  /**
   * Set general profile data
   */
  const setGeneralProfile = async (data: GeneralProfileData) => {
    state.profileData.general = data;
    state.selectedUserTypes = Array.isArray(data.userTypes) ? [...data.userTypes] : [];

    // Check if we need to link an identity - but don't let failures break the flow
    try {
      if (!state.isEmail && data.email && deps.requestEmailLinking) {
        // User signed up with phone, wants to add email
        await deps.requestEmailLinking(data.email);
      } else if (state.isEmail && data.phoneNumber && deps.requestPhoneLinking) {
        // User signed up with email, wants to add phone
        await deps.requestPhoneLinking(data.phoneNumber);
      }
    } catch (error) {
      // Log the error but don't break the profile flow
      logger.error('Identity linking failed, continuing without linking:', error);
      // Clear any pending identity link state to prevent getting stuck
      state.pendingIdentityLink = { type: null, value: '', otpSent: false };
    }
  };

  /**
   * Profile Data Setters
   *
   * These setters store form data in state using local ProfileData types.
   * The state is typed to accept these local types (not GraphQL Portfolio types)
   * since form data has optional fields and simplified structures.
   * Conversion to GraphQL types happens in submitRoleProfile when data is sent to the API.
   */
  const setTeacherProfile = (data: TeacherProfileData) => {
    state.profileData.teacher = data;
  };

  const setStudentProfile = (data: StudentProfileData) => {
    state.profileData.student = data;
  };

  const setParentProfile = (data: ParentProfileData) => {
    state.profileData.parent = data;
  };

  const setEmployerProfile = (data: EmployerProfileData) => {
    state.profileData.employer = data;
  };

  const setSchoolProfile = (data: SchoolProfileData) => {
    state.profileData.school = data;
  };

  const setOrganizerProfile = (data: OrganizerProfileData) => {
    state.profileData.organizer = data;
  };

  /**
   * Submit general profile data
   */
  const submitGeneralProfile = async (data: GeneralProfileData) => {
    state.loading = true;
    state.error = null;

    try {
      // Layer 1 Fix: Check token exists before making GraphQL request with exponential backoff
      // This prevents "Authentication required" errors from race conditions
      const tokenResult = await waitForToken({
        context: 'profile_submission',
        metadata: { phase: 'general_profile' },
        logger
      });

      if (!tokenResult.token) {
        state.error = 'Session not ready. Please wait a moment and try again.';
        state.loading = false;
        throw new Error('Authentication token not available. Please try again.');
      }

      // Capture browser timezone for behavioral analytics
      // This allows accurate rhythm classification (e.g., "Early Bird" means 5-8am in user's local time)
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // General profile data is stored in User, not a separate portfolio
      const result = await deps.updatePortfolioMutate({
        input: {
          type: PortfolioUpdateType.User,
          data: {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            userTypes: data.userTypes,
            timezone: browserTimezone // IANA timezone string (e.g., "America/Los_Angeles")
          }
        }
      });

      // Check for GraphQL errors OR mutation failure
      if (result?.errors || !result?.data?.updatePortfolio?.success) {
        const errorMessage =
          result?.errors?.[0]?.message ||
          result?.data?.updatePortfolio?.message ||
          'Failed to update profile';
        logger.error('Failed to submit general profile:', errorMessage);
        state.error = errorMessage;
        throw new Error(errorMessage);
      }

      if (result?.data?.updatePortfolio?.success) {
        // Update local state
        await setGeneralProfile(data);

        // Save slug if provided (non-blocking)
        if (data.slug) {
          try {
            const slugResult = await nexusClient.mutate<SetMyProfileSlugResponse>({
              mutation: SET_MY_PROFILE_SLUG_MUTATION,
              variables: { slug: data.slug }
            });

            if (slugResult.data?.setMyProfileSlug?.success) {
              logger.log('Profile slug saved successfully:', data.slug);
            } else {
              logger.warn('Failed to save slug:', slugResult.data?.setMyProfileSlug?.error);
              // Don't fail the whole flow for slug issues
            }
          } catch (slugError) {
            logger.warn('Error saving slug (non-blocking):', slugError);
            // Don't fail the whole flow for slug issues
          }
        }

        // Update portfolio completion status from response
        if (result?.data?.updatePortfolio?.portfolio?.completionStatus) {
          state.portfolioCompletionStatus = result.data.updatePortfolio.portfolio.completionStatus;
        }

        // Save session for recovery
        profileStorage.saveSession({
          phase: state.phase,
          profileData: state.profileData as any, // Type assertion: state uses GraphQL types, storage expects local types
          selectedUserTypes: state.selectedUserTypes,
          currentRoleIndex: state.currentRoleIndex,
          contact: state.contact,
          isEmail: state.isEmail
        });

        // Proceed to next phase if no identity linking is pending
        if (!state.pendingIdentityLink.otpSent && deps.nextPhase) {
          deps.nextPhase();
        }
      } else {
        throw new Error(result?.data?.updatePortfolio?.message || ERROR_MESSAGES.GENERIC);
      }
    } catch (error) {
      logger.error('Failed to submit general profile:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.PROFILE_INCOMPLETE;
      throw error;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Submit role-specific profile data
   */
  const submitRoleProfile = async (roleType: string, data: Record<string, unknown>) => {
    state.loading = true;
    state.error = null;

    try {
      // Layer 1 Fix: Check token exists before making GraphQL request with exponential backoff
      const tokenResult = await waitForToken({
        context: 'role_profile_submission',
        metadata: { phase: 'role_profile', roleType },
        logger
      });

      if (!tokenResult.token) {
        state.error = 'Session not ready. Please wait a moment and try again.';
        state.loading = false;
        throw new Error('Authentication token not available. Please try again.');
      }

      // Map role type to PortfolioUpdateType enum - using strong typing
      const typeMap: Record<string, PortfolioUpdateType> = {
        teacher: PortfolioUpdateType.Teacher,
        student: PortfolioUpdateType.Student,
        parent: PortfolioUpdateType.Parent,
        employer: PortfolioUpdateType.Employer,
        school: PortfolioUpdateType.School,
        organizer: PortfolioUpdateType.Organizer
      };

      const portfolioType = typeMap[roleType.toLowerCase()];
      if (!portfolioType) {
        throw new Error(`Unknown role type: ${roleType}`);
      }

      // Remove _id and any nested type/payload fields if present (cleanup from old format)
      const { _id, type, payload, ...cleanData } = data as any;

      // Use the actual data, whether it was nested in payload or not
      const actualData: Record<string, unknown> = (payload as Record<string, unknown>) || cleanData;

      // Create properly typed input for the mutation
      const input: UpdatePortfolioInput = {
        type: portfolioType,
        data: actualData
      };

      logger.log(`Submitting ${roleType} profile with type:`, portfolioType);
      const result = await deps.updatePortfolioMutate({ input });

      // Check for GraphQL errors OR mutation failure
      if (result?.errors || !result?.data?.updatePortfolio?.success) {
        const errorMessage =
          result?.errors?.[0]?.message ||
          result?.data?.updatePortfolio?.message ||
          'Failed to update profile';
        logger.error(`Failed to submit ${roleType} profile:`, errorMessage);
        state.error = errorMessage;
        // Don't proceed to next phase on error
        return; // Exit early on error - loading state cleared in finally block
      }

      if (result?.data?.updatePortfolio?.success) {
        // Update local state based on role type
        switch (roleType.toLowerCase()) {
          case 'teacher':
            setTeacherProfile(actualData as unknown as TeacherProfileData);
            break;
          case 'student':
            setStudentProfile(actualData as unknown as StudentProfileData);
            break;
          case 'parent':
            setParentProfile(actualData as unknown as ParentProfileData);
            break;
          case 'employer':
            setEmployerProfile(actualData as unknown as EmployerProfileData);
            break;
          case 'school':
            setSchoolProfile(actualData as unknown as SchoolProfileData);
            break;
          case 'organizer':
            setOrganizerProfile(actualData as unknown as OrganizerProfileData);
            break;
          default:
            logger.warn(`Unknown role type: ${roleType}`);
            break;
        }

        // Update portfolio completion status from response
        if (result?.data?.updatePortfolio?.portfolio?.completionStatus) {
          state.portfolioCompletionStatus = result.data.updatePortfolio.portfolio.completionStatus;
        }

        // Save session after successful update
        profileStorage.saveSession({
          phase: state.phase,
          profileData: state.profileData as any, // Type assertion: state uses GraphQL types, storage expects local types
          selectedUserTypes: state.selectedUserTypes,
          currentRoleIndex: state.currentRoleIndex,
          contact: state.contact,
          isEmail: state.isEmail
        });

        // Move to next role or complete
        state.currentRoleIndex++;
        if (state.currentRoleIndex < state.selectedUserTypes.length) {
          // Move to next role profile phase
          const nextRole = state.selectedUserTypes[state.currentRoleIndex];
          if (deps.navigationService) {
            await deps.navigationService.navigate(`${nextRole.toLowerCase()}Profile` as AuthPhase);
          } else {
            state.phase = `${nextRole.toLowerCase()}Profile` as AuthPhase; // Fallback
          }
          logger.log(`Moving to next role: ${nextRole}`);
        } else {
          // All profiles complete - just mark as complete and redirect
          // Do NOT call completeProfileAndRedirect as it would re-save everything
          logger.log('All role profiles complete, finishing setup');

          // Mark profile as complete in localStorage
          localStorage.setItem('profileComplete', 'true');
          if (deps.rootStore) {
            deps.rootStore.commit(`user/${UserMutationTypes.SET_PROFILE_COMPLETE}`, true);
          }

          // Clear session storage
          profileStorage.clearSession();

          // Move to complete phase and redirect
          if (deps.navigationService) {
            // NavigationService will handle the complete flow and trigger redirect
            await deps.navigationService.completeFlow(state.originalRedirect);
          } else {
            // Fallback if no navigation service
            state.phase = 'complete';
            if (deps.completeAndRedirect) {
              await deps.completeAndRedirect();
            }
          }
        }
      } else {
        throw new Error(result?.data?.updatePortfolio?.message || ERROR_MESSAGES.GENERIC);
      }
    } catch (error) {
      logger.error(`Failed to submit ${roleType} profile:`, error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.PROFILE_INCOMPLETE;
    } finally {
      state.loading = false;
    }
  };

  return {
    determineIncompletePhase,
    setGeneralProfile,
    setTeacherProfile,
    setStudentProfile,
    setParentProfile,
    setEmployerProfile,
    setSchoolProfile,
    setOrganizerProfile,
    submitGeneralProfile,
    submitRoleProfile
  };
}
